import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import permissionService from '../../../services/permissionService';
import SkeletonLoader from '../../../components/SkeletonLoader/SkeletonLoader';
import './RoleList.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { matchesWordStartAny } from '../../../utils/searchMatch';

const RoleList = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.getRoles();

      if (response.success) {
        setRoles(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch roles');
        toast.error(response.message || 'Failed to fetch roles');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch roles';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterAndPaginateRoles = useCallback(() => {
    let filtered = [...roles];

    // Search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter((role) =>
        matchesWordStartAny([role.id, role.role_name], searchTerm)
      );
    }

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    // Pagination
    const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(filtered.length, 1) : itemsPerPage;
    const startIndex = (currentPage - 1) * effectiveItemsPerPage;
    const endIndex = startIndex + effectiveItemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    setFilteredRoles(paginated);
  }, [roles, searchTerm, currentPage, itemsPerPage, sortConfig]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    filterAndPaginateRoles();
  }, [filterAndPaginateRoles]);

  const handleAddRole = () => {
    navigate('/permission/roles/add');
  };

  const handleEditRole = (roleId) => {
    navigate(`/permission/roles/edit/${roleId}`);
  };

  const handleAssignPermission = (roleId) => {
    navigate(`/permission/roles/${roleId}/assign`);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 9999 : Number(value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(Number(newPage));
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <i className="fa fa-sort" aria-hidden="true" style={{ marginLeft: '5px', color: '#ccc' }}></i>;
    }
    if (sortConfig.direction === 'ascending') {
      return <i className="fa fa-sort-asc" aria-hidden="true" style={{ marginLeft: '5px', color: '#333' }}></i>;
    }
    return <i className="fa fa-sort-desc" aria-hidden="true" style={{ marginLeft: '5px', color: '#333' }}></i>;
  };

  const getFilteredTotal = () => {
    if (searchTerm.trim() === '') return roles.length;
    return roles.filter((role) =>
      matchesWordStartAny([role.id, role.role_name], searchTerm)
    ).length;
  };

  const totalFiltered = getFilteredTotal();
  const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(totalFiltered, 1) : itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / effectiveItemsPerPage));

  if (loading) {
    return (
      <div className="role-list-page list-screen-page-container">
        <div className="role-list-header-flex list-screen-top-bar">
          <h2 className="role-list-title list-screen-title-text">Role List</h2>
          <div className="role-list-header-actions">
            <ListScreenSearchBar
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              inputId="role-list-search"
            />
            <button onClick={handleAddRole} className="role-list-add-btn list-screen-add-btn">
              Add Role
            </button>
          </div>
        </div>
        <div className="role-list-container">
          <SkeletonLoader type="mediaRows" count={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="role-list-page list-screen-page-container">
      {/* Header outside table container */}
      <div className="role-list-header-flex list-screen-top-bar">
        <h2 className="role-list-title list-screen-title-text">Role List</h2>
        <div className="role-list-header-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            inputId="role-list-search"
          />
          <button onClick={handleAddRole} className="role-list-add-btn list-screen-add-btn">
            Add Role
          </button>
        </div>
      </div>

      <div className="role-list-container">

        {error && (
          <div className="role-list-error-message">
            <p>{error}</p>
            <button onClick={fetchRoles} className="role-list-retry-btn">Retry</button>
          </div>
        )}

        <div className="role-list-table-wrapper">
          <table className="role-list-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('id')} style={{ cursor: 'pointer' }}>
                  Role Id {getSortIcon('id')}
                </th>
                <th onClick={() => requestSort('role_name')} style={{ cursor: 'pointer' }}>
                  Role Name {getSortIcon('role_name')}
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.length === 0 ? (
                <tr>
                  <td colSpan="3" className="role-list-no-data">
                    {searchTerm ? 'No roles found matching your search' : 'No roles found'}
                  </td>
                </tr>
              ) : (
                filteredRoles.map((role) => (
                  <tr key={role.id}>
                    <td>{role.id}</td>
                    <td>{role.role_name}</td>
                    <td className="role-list-action-cell">
                      <button
                        onClick={() => handleEditRole(role.id)}
                        className="role-list-action-btn role-list-edit-btn"
                        title="Edit"
                      >
                        <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                      </button>
                      {role.id !== 1 && (
                        <button
                          onClick={() => handleAssignPermission(role.id)}
                          className="role-list-action-btn role-list-assign-btn"
                          title="Assign Permission"
                        >
                          <i className="fa fa-user-plus" aria-hidden="true"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>
      {totalFiltered > 0 && (
        <div className="role-list-pagination-outside">
          <ListScreenPaginationBar
            entriesValue={itemsPerPage === 9999 ? 'all' : String(itemsPerPage)}
            onEntriesChange={handleItemsPerPageChange}
            showPageSelector
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default RoleList;
